import {
  APPLICATION_STEPS,
  AVERAGE_DAILY_ROOM_RATE_LIMIT,
  AVERAGE_ROOM_RATE_CODE,
  BINARY_CONSTANTS,
  BUSINESS_WEBSITE,
  CALL_FOR_XMOD_FAILED_QUESTION_CODE,
  dateFormatWithMonthName,
  FEIN_FIELD_FOR_MANAGEMENT_FINANCIALS,
  HAS_SCHEDULED_BUILDINGS_CODE,
  HAS_TBI_BUILDING_CODE,
  HAS_TBI_CODE,
  HAULS_NON_OWNED_GOODS_VEHICLE,
  IS_ANY_HAULS_NON_OWNED_GOODS_VEHICLE,
  NUM_EMPLOYEES_FIELD_CODE,
  ONLY_HOME_OFFICE_CODE,
  ONLY_HOME_OFFICE_OPTION,
  OPERATES_FROM_COMMERCIAL_SPACE_CODE,
  PREMISES_FORM_CODE,
  SCHEDULED_BUILDING_OCCUPANCY_VALUES,
  SUBCONTRACTORS_FIELD_CODE,
  TBI_BUILDING_OCCUPANCY_VALUES,
  UNEMPLOYMENT_NUMBER_UW_QUESTION,
  UW_VEHICLE_GROUP_CODE,
  VEHICLE_FORM_CODE,
} from 'common/constants';
import type { Field, FormSet, FormSetField } from 'components/lib/QuestionEngine';
import {
  formatDate,
  hasScheduledBuildingCondition,
  rewriteScheduledBuildingConditions,
} from 'helpers/utils';
import { isEmpty } from 'lodash-es';
import { DateTime } from 'luxon';
import type {
  ExposureOrGroup,
  Exposures,
  Groups,
  Submission,
  TransformedSubmission,
} from './submission.types';

/**
 * Collapse a page's groups into one entry keyed by the page, so a renamed group cannot silently
 * drop its questions out of the flow (ENG-12240).
 */
const mergePageGroups = (fields: FormSetField[], code: string, name: string): FormSetField[] => {
  if (isEmpty(fields)) return fields;

  return [
    {
      ...fields[0],
      code,
      name,
      nested_fields: fields.flatMap((field) => field.nested_fields ?? []),
    },
  ];
};

/**
 * Flatten the underwriting questions and question codes
 */
export const underwritingQuestionsSelector = (questions: FormSet[]) => {
  const isPremisesGroup = (question: FormSetField): boolean =>
    question.code === APPLICATION_STEPS['uw-property'].key;

  const isVehicleGroup = (question: FormSetField): boolean =>
    question.code === UW_VEHICLE_GROUP_CODE;

  const flattenQuestions = questions
    .flatMap((section) => {
      const sectionFields = (section.fields as FormSetField[]) ?? [];

      const pageFields =
        section.code === PREMISES_FORM_CODE
          ? mergePageGroups(
              sectionFields,
              APPLICATION_STEPS['uw-property'].key,
              'About your properties',
            )
          : section.code === VEHICLE_FORM_CODE
            ? mergePageGroups(sectionFields, UW_VEHICLE_GROUP_CODE, 'Underwriting Questions')
            : sectionFields;

      // eslint-disable-next-line complexity
      return pageFields.map((question) => {
        if (question.code === APPLICATION_STEPS['management_&_financials'].key) {
          const subcontractorsQuestions = section?.fields?.find(
            (field) => field.code === SUBCONTRACTORS_FIELD_CODE,
          )?.nested_fields;

          const filteredQuestion = question.nested_fields?.filter(
            (field) =>
              field.code !== UNEMPLOYMENT_NUMBER_UW_QUESTION.fieldCode &&
              field.code !== UNEMPLOYMENT_NUMBER_UW_QUESTION.questionCode,
          );

          const numEmployeesIndex =
            filteredQuestion?.findIndex((field) => field.code === NUM_EMPLOYEES_FIELD_CODE) ?? -1;
          const feinInsertIndex = numEmployeesIndex >= 0 ? numEmployeesIndex + 1 : 0;
          const baseFields = (filteredQuestion ?? []).map(
            (field): Field =>
              field.code === NUM_EMPLOYEES_FIELD_CODE
                ? { ...field, hide_asteriks_in_question_text: true }
                : field,
          );
          const nestedFieldsWithFein = [
            ...baseFields.slice(0, feinInsertIndex),
            FEIN_FIELD_FOR_MANAGEMENT_FINANCIALS,
            ...baseFields.slice(feinInsertIndex),
          ];

          return {
            ...question,
            nested_fields: [
              ...nestedFieldsWithFein,
              ...(!isEmpty(subcontractorsQuestions) ? subcontractorsQuestions! : []),
            ],
          };
        }

        if (question.code === APPLICATION_STEPS.business_contact.key) {
          return {
            ...question,
            nested_fields: question.nested_fields?.reduce((acc, field) => {
              if (!['contactEmail', 'businessWebsite'].includes(field.code)) {
                const updatedField = { ...field };

                if (field.code === 'contactFirst') {
                  updatedField.name = 'First Name';
                  updatedField.question_text = "Let's start with your name and phone number";
                  updatedField.hide_asteriks_in_question_text = true;
                } else if (field.code === 'contactLast') {
                  updatedField.name = 'Last Name';
                } else if (field.code === 'contactPhone') {
                  updatedField.name = 'Phone Number';
                }

                acc.push(updatedField);
              }

              return acc;
            }, [] as Field[]),
          };
        }

        if (isPremisesGroup(question)) {
          return {
            ...question,
            code: APPLICATION_STEPS['uw-property'].key,
            name: 'About your properties',
            nested_fields: question.nested_fields?.map((field) => {
              if (hasScheduledBuildingCondition(field?.show_condition)) {
                return {
                  ...field,
                  type: 'select_button',
                  show_condition: rewriteScheduledBuildingConditions(field?.show_condition),
                } satisfies Field;
              }
              if (field.code === HAS_TBI_CODE) {
                return {
                  ...field,
                  type: 'select_button',
                  show_condition: {
                    reference: HAS_TBI_BUILDING_CODE,
                    relational_operator: '==',
                    value: true,
                    value_type: 'boolean',
                  },
                } satisfies Field;
              }

              return field;
            }),
          };
        }

        if (
          question.code === APPLICATION_STEPS.property.key &&
          question.nested_fields?.some(
            (field) => field.code === OPERATES_FROM_COMMERCIAL_SPACE_CODE,
          )
        ) {
          return {
            ...question,
            code: APPLICATION_STEPS['before-property'].key,
            name: 'Your Properties',
          };
        }

        if (question.code === APPLICATION_STEPS.classification.key) {
          return {
            ...question,
            nested_fields: question.nested_fields?.map((field) => {
              if (field.code === AVERAGE_ROOM_RATE_CODE) {
                return {
                  ...field,
                  additional_data: {
                    ...field.additional_data,
                    max: AVERAGE_DAILY_ROOM_RATE_LIMIT,
                  },
                } satisfies Field;
              }

              return field;
            }),
          };
        }

        if (isVehicleGroup(question)) {
          return {
            ...question,
            nested_fields: question.nested_fields?.map((field) => {
              if (field.code === HAULS_NON_OWNED_GOODS_VEHICLE) {
                return {
                  ...field,
                  show_condition: {
                    ...field.show_condition,
                    operations: [
                      {
                        reference: IS_ANY_HAULS_NON_OWNED_GOODS_VEHICLE,
                        relational_operator: '==',
                        value: true,
                        value_type: 'boolean',
                      },
                      field.show_condition?.operations?.[1] ?? {},
                    ],
                  },
                } satisfies Field;
              }

              return field;
            }),
          };
        }

        return question;
      });
    })
    // Drop `callForXMODFailed` at the schema level instead of via `handleShowCondition`:
    // QuestionEngineFieldParser nulls out values for fields hidden by `handleShowCondition`,
    // which would destroy the persisted `Yes` flag the backend relies on for XMOD failure handling.
    .map((question) => ({
      ...question,
      nested_fields: question.nested_fields?.filter(
        (field) => field.code !== CALL_FOR_XMOD_FAILED_QUESTION_CODE,
      ),
    }));

  return {
    flattenQuestions,
    allQuestionCodes: [
      ...flattenQuestions.flatMap((qst) => qst.nested_fields?.map((field) => field.code) || []),
      NUM_EMPLOYEES_FIELD_CODE,
      BUSINESS_WEBSITE,
    ],
    allQuestions: questions,
  };
};

/**
 * Transform exposures and group values into array
 */
export const submissionSelector = (submission: Submission) => {
  const { exposures, groups } = submission.data;

  const transform = (object: Submission['data']['exposures'] | Submission['data']['groups']) =>
    Object.entries(object).reduce((acc, [key, value]) => {
      acc[key] = Object.entries(value).map(([id, data]) => ({
        id,
        name: key,
        data,
        perils: data.perils || [],
      }));

      return acc;
    }, {});

  const startDate = formatDate(submission?.effective_date, dateFormatWithMonthName);

  const endDate = formatDate(
    DateTime.fromISO(submission?.effective_date).plus({ years: 1 }).toISODate(),
    dateFormatWithMonthName,
  );

  const policyTerms = `${startDate} - ${endDate}`;

  const exposuresObject = transform(exposures) as { [key in Exposures]: ExposureOrGroup[] };
  const groupsObject = transform(groups) as { [key in Groups]: ExposureOrGroup[] };

  let hasScheduledBuildings = false;
  let hasTBIBuildings = false;
  let allBuildingsAreHomeOffice = BINARY_CONSTANTS.YES;

  if (exposuresObject.building) {
    exposuresObject.building.forEach((building) => {
      if (SCHEDULED_BUILDING_OCCUPANCY_VALUES.includes(building.data.bdg_occupancy)) {
        hasScheduledBuildings = true;
      }
      if (TBI_BUILDING_OCCUPANCY_VALUES.includes(building.data?.bdg_occupancy)) {
        hasTBIBuildings = true;
      }
      if (building?.data?.bdg_occupancy !== ONLY_HOME_OFFICE_OPTION) {
        allBuildingsAreHomeOffice = BINARY_CONSTANTS.NO;
      }
    });
  }

  if (submission?.data?.uw_questions?.operatesFromCommercialSpace === BINARY_CONSTANTS.NO) {
    allBuildingsAreHomeOffice = BINARY_CONSTANTS.YES;
  }

  return {
    ...submission,
    policyTerms,
    data: {
      ...submission.data,
      uw_questions: {
        ...submission.data.uw_questions,
        [HAS_SCHEDULED_BUILDINGS_CODE]: hasScheduledBuildings,
        [HAS_TBI_BUILDING_CODE]: hasTBIBuildings,
        [ONLY_HOME_OFFICE_CODE]: allBuildingsAreHomeOffice,
      },
      exposures: exposuresObject,
      groups: groupsObject,
    },
  } as TransformedSubmission;
};
